import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Lock, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "@/i18n/LanguageContext";
import VangardHeader from "@/components/vangard/VangardHeader";
import VangardFooter from "@/components/vangard/VangardFooter";
import VMark from "@/components/vangard/VMark";
import Seo, { breadcrumbSchema } from "@/components/Seo";

const QUICK_KEYS = ["vc.conc.q1", "vc.conc.q2", "vc.conc.q3", "vc.conc.q4", "vc.conc.q5"];
const RESPONSE_KEYS = ["vc.conc.r1", "vc.conc.r2", "vc.conc.r3", "vc.conc.r4"];

function LockedScreen({ navigate, t, mode, status }) {
  const signedOut = mode === "signed-out";
  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid={signedOut ? "concierge-locked" : "concierge-gated"}>
      <Seo
        title={t("vc.seo.concierge.title")}
        description={t("vc.seo.concierge.desc")}
        schema={[
          breadcrumbSchema([{ name: "VangardClub", path: "/" }, { name: "Concierge", path: "/concierge" }]),
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: "VangardClub Concierge",
            serviceType: "24/7 luxury personal concierge",
            provider: { "@type": "Organization", name: "VangardClub" },
            areaServed: "Worldwide",
            availableChannel: { "@type": "ServiceChannel", serviceUrl: "https://vangardclub.com/concierge" },
          },
        ]}
      />
      <VangardHeader />
      <section className="relative overflow-hidden pt-[92px]">
        <VMark position="center" size={900} opacity={0.05} />
        <div className="relative max-w-[900px] mx-auto px-6 lg:px-10 py-24 lg:py-32 text-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="inline-flex w-16 h-16 rounded-full border border-white/20 items-center justify-center mx-auto">
              <Lock size={22} />
            </div>
            <div className="mt-8 text-[11px] tracking-[0.35em] uppercase text-neutral-400">
              {signedOut ? t("vc.conc.eyebrow") : t(`vc.onb.status.${status || "none"}`)}
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl mt-4 leading-[1.05]">
              {signedOut ? (
                <>{t("vc.conc.locked-title-1")} <span className="italic text-neutral-300">{t("vc.conc.locked-italic")}</span></>
              ) : (
                <>{t("vc.lock.title-1")} <span className="italic text-neutral-300">{t("vc.lock.italic")}</span></>
              )}
            </h1>
            <p className="mt-6 text-neutral-400 font-light leading-relaxed max-w-[52ch] mx-auto">
              {signedOut ? t("vc.conc.locked-body") : t("vc.lock.body")}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
              {signedOut ? (
                <>
                  <button onClick={() => navigate("/login")} className="inline-flex items-center gap-3 bg-white text-black px-8 py-4 rounded-full text-[12px] tracking-[0.25em] uppercase font-medium hover:bg-neutral-200 transition-colors" data-testid="concierge-login-btn">
                    {t("vc.auth.sign-in")} <ArrowRight size={14} />
                  </button>
                  <button onClick={() => navigate("/register")} className="inline-flex items-center gap-3 border border-white/25 text-white px-8 py-4 rounded-full text-[12px] tracking-[0.25em] uppercase hover:bg-white/5 hover:border-white/55 transition-colors" data-testid="concierge-register-btn">
                    {t("vc.auth.join-vangard")}
                  </button>
                </>
              ) : (
                <button onClick={() => navigate("/onboarding")} className="inline-flex items-center gap-3 bg-white text-black px-8 py-4 rounded-full text-[12px] tracking-[0.25em] uppercase font-medium hover:bg-neutral-200 transition-colors" data-testid="gated-open-application">
                  {status === "none" ? t("vc.lock.cta-start") : t("vc.lock.cta-onb")} <ArrowRight size={14} />
                </button>
              )}
            </div>
          </motion.div>
        </div>
      </section>
      <VangardFooter />
    </div>
  );
}

export default function ConciergePage() {
  const navigate = useNavigate();
  const { user, loading, initialized } = useAuth();
  const { t } = useTranslation();
  const [messages, setMessages] = useState([{ id: 1, type: "concierge", text: t("vc.conc.welcome"), time: t("vc.conc.now") }]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = (text) => {
    if (!text.trim()) return;
    setMessages((prev) => [...prev, { id: Date.now(), type: "user", text: text.trim(), time: t("vc.conc.now") }]);
    setInputValue("");
    setIsTyping(true);
    setTimeout(() => {
      const responses = RESPONSE_KEYS.map((k) => t(k));
      setMessages((prev) => [
        ...prev,
        { id: Date.now() + 1, type: "concierge", text: responses[Math.floor(Math.random() * responses.length)], time: t("vc.conc.now") },
      ]);
      setIsTyping(false);
    }, 1200);
  };

  if (!initialized || loading) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center" data-testid="concierge-loading">
        <div className="w-10 h-10 rounded-full border-2 border-white/70 border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <LockedScreen navigate={navigate} t={t} mode="signed-out" />;
  }

  // Application gating — concierge is unlocked only when approved
  const status = user?.application_status;
  if (status !== "approved") {
    return <LockedScreen navigate={navigate} t={t} mode="pending" status={status} />;
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col" data-testid="concierge-page">
      <Seo title={t("vc.seo.concierge.title")} description={t("vc.seo.concierge.desc")} noindex />
      <VangardHeader />

      <div className="flex-1 flex flex-col max-w-[900px] w-full mx-auto pt-[92px] px-6 lg:px-10 pb-6">
        <div className="text-[11px] tracking-[0.35em] uppercase text-neutral-400">{t("vc.conc.eyebrow")}</div>
        <h1 className="font-serif text-4xl mt-3">{t("vc.conc.chat-title")}</h1>
        <p className="mt-3 text-sm text-neutral-400 font-light">
          {t("vc.conc.chat-sub")}
        </p>

        <div className="mt-8 flex-1 rounded-3xl border border-white/[0.08] bg-[#0A0A0A] flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
            <AnimatePresence mode="popLayout">
              {messages.map((m) => (
                <motion.div
                  key={m.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  className={`flex ${m.type === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[75%] rounded-2xl px-5 py-3 text-sm leading-relaxed ${
                      m.type === "user"
                        ? "bg-white text-black rounded-br-md"
                        : "bg-white/5 border border-white/10 text-neutral-200 rounded-bl-md"
                    }`}
                  >
                    {m.text}
                    <div className={`text-[10px] mt-2 tracking-widest uppercase ${m.type === "user" ? "text-black/50" : "text-neutral-500"}`}>
                      {m.time}
                    </div>
                  </div>
                </motion.div>
              ))}
              {isTyping && (
                <div className="flex justify-start">
                  <div className="rounded-2xl px-5 py-3 bg-white/5 border border-white/10">
                    <div className="flex gap-1">
                      <motion.div animate={{ y: [0, -4, 0] }} transition={{ repeat: Infinity, duration: 0.6 }} className="w-1.5 h-1.5 rounded-full bg-white/60" />
                      <motion.div animate={{ y: [0, -4, 0] }} transition={{ repeat: Infinity, duration: 0.6, delay: 0.1 }} className="w-1.5 h-1.5 rounded-full bg-white/60" />
                      <motion.div animate={{ y: [0, -4, 0] }} transition={{ repeat: Infinity, duration: 0.6, delay: 0.2 }} className="w-1.5 h-1.5 rounded-full bg-white/60" />
                    </div>
                  </div>
                </div>
              )}
            </AnimatePresence>
            <div ref={messagesEndRef} />
          </div>

          {messages.length <= 2 && (
            <div className="px-6 pb-4">
              <div className="text-[10px] tracking-[0.35em] uppercase text-neutral-500 mb-3">{t("vc.conc.quick")}</div>
              <div className="flex flex-wrap gap-2">
                {QUICK_KEYS.map((key, idx) => (
                  <button
                    key={key}
                    onClick={() => send(t(key))}
                    className="text-[12px] px-4 py-2 rounded-full bg-transparent text-neutral-300 border border-white/15 hover:bg-white/5 hover:border-white/45 transition-colors"
                    data-testid={`quick-action-${idx}`}
                  >
                    {t(key)}
                  </button>
                ))}
              </div>
            </div>
          )}

          <form
            onSubmit={(e) => { e.preventDefault(); send(inputValue); }}
            className="border-t border-white/[0.08] px-4 py-4 flex gap-3"
          >
            <input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={t("vc.conc.placeholder")}
              className="flex-1 bg-transparent border border-white/[0.12] rounded-full text-white text-sm px-5 py-3 focus:border-white/45 focus:outline-none transition-colors placeholder:text-neutral-500"
              data-testid="chat-input"
            />
            <button
              type="submit"
              disabled={!inputValue.trim()}
              className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-white text-black hover:bg-neutral-200 disabled:opacity-40 transition-colors"
              data-testid="chat-send-btn"
              aria-label="Send"
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      </div>

      <VangardFooter />
    </div>
  );
}
